import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatDateTime } from '../utils/dateFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  MessageSquare,
  Mail,
  Phone,
  Send,
  Loader2,
  Clock,
  CheckCircle2,
  AlertCircle,
  ArrowUpRight,
  ArrowDownLeft,
  FileText,
  Edit3
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CommunicationsPanel = ({ 
  participantId, 
  participantName,
  participantPhone,
  participantEmail,
  caseId = null,
  onCommunicationSent = () => {}
}) => {
  const [activeTab, setActiveTab] = useState('sms');
  const [communications, setCommunications] = useState([]);
  const [openCases, setOpenCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingComm, setSendingComm] = useState(false);
  
  // Templates
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  
  // Dialog states
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const [completeCallDialogOpen, setCompleteCallDialogOpen] = useState(false);
  const [activeCallId, setActiveCallId] = useState(null);
  
  // Outbound numbers for Send From dropdown
  const [outboundNumbers, setOutboundNumbers] = useState([]);
  const [selectedFromNumber, setSelectedFromNumber] = useState('');
  
  // Form states
  const [selectedCaseId, setSelectedCaseId] = useState(caseId || '');
  
  // SMS form
  const [smsIsFreeForm, setSmsIsFreeForm] = useState(true);
  const [smsTemplateId, setSmsTemplateId] = useState('');
  const [smsMessage, setSmsMessage] = useState('');
  const [smsPreviewLoading, setSmsPreviewLoading] = useState(false);
  
  // Email form
  const [emailIsFreeForm, setEmailIsFreeForm] = useState(true);
  const [emailTemplateId, setEmailTemplateId] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailPreviewLoading, setEmailPreviewLoading] = useState(false);
  
  // Call form
  const [callNotes, setCallNotes] = useState('');
  const [callDuration, setCallDuration] = useState('');

  useEffect(() => {
    if (participantId) {
      fetchCommunications();
      fetchTemplates();
      fetchOutboundNumbers();
      if (!caseId) {
        fetchOpenCases();
      }
    }
  }, [participantId, caseId]);

  const fetchCommunications = async () => {
    setLoading(true);
    try {
      const endpoint = caseId 
        ? `${API}/communications/case/${caseId}`
        : `${API}/communications/participant/${participantId}`;
      const response = await axios.get(endpoint);
      setCommunications(response.data.communications || []);
    } catch (error) {
      console.error('Failed to fetch communications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const [smsRes, emailRes, adminRes] = await Promise.all([
        axios.get(`${API}/message-templates?channel=sms`),
        axios.get(`${API}/message-templates?channel=email`),
        axios.get(`${API}/admin/templates`).catch(() => ({ data: { templates: [] } }))
      ]);
      
      // SMS templates from message_templates plus text templates saved under Message Templates
      const adminSmsTemplates = (adminRes.data?.templates || [])
        .filter(t => t.category === 'sms' && t.is_active !== false)
        .map(t => ({ ...t, body: t.body || '', source: 'admin_templates' }));
      setSmsTemplates([...(smsRes.data || []), ...adminSmsTemplates].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      
      // Email templates: combine message_templates (channel=email) with admin templates (category=email)
      const messageEmailTemplates = emailRes.data || [];
      const adminTemplates = adminRes.data?.templates || [];
      
      // Filter admin templates to only include email category and active ones
      const adminEmailTemplates = adminTemplates
        .filter(t => t.category === 'email' && t.is_active !== false)
        .map(t => ({
          ...t,
          // Normalize field names to match message_templates format
          id: t.id,
          name: t.name,
          subject: t.subject || '',
          body: t.body || '',
          merge_fields: t.merge_fields || [],
          source: 'admin_templates'
        }));
      
      // Combine and dedupe by name (prefer admin templates)
      const combinedEmailTemplates = [...adminEmailTemplates];
      const adminNames = new Set(adminEmailTemplates.map(t => t.name.toLowerCase()));
      
      for (const template of messageEmailTemplates) {
        if (!adminNames.has(template.name.toLowerCase())) {
          combinedEmailTemplates.push({ ...template, source: 'message_templates' });
        }
      }
      
      // Sort alphabetically
      combinedEmailTemplates.sort((a, b) => a.name.localeCompare(b.name));
      
      setEmailTemplates(combinedEmailTemplates);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchOpenCases = async () => {
    try {
      const response = await axios.get(`${API}/participants/${participantId}/open-cases`);
      setOpenCases(response.data.open_cases || []);
      if (response.data.open_cases?.length > 0 && !selectedCaseId) {
        setSelectedCaseId(response.data.open_cases[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch open cases:', error);
    }
  };

  const fetchOutboundNumbers = async () => {
    try {
      const response = await axios.get(`${API}/outbound-numbers/available`);
      const numbers = response.data.outbound_numbers || [];
      setOutboundNumbers(numbers);
      // Set default number
      const defaultNumber = numbers.find(n => n.is_default);
      if (defaultNumber) {
        setSelectedFromNumber(defaultNumber.phone_number);
      } else if (numbers.length > 0) {
        setSelectedFromNumber(numbers[0].phone_number);
      }
    } catch (error) {
      console.error('Failed to fetch outbound numbers:', error);
    }
  };

  const handleSmsTemplateSelect = async (templateId) => {
    setSmsTemplateId(templateId);
    if (templateId && templateId !== 'free-form') {
      setSmsPreviewLoading(true);
      try {
        const response = await axios.post(`${API}/message-templates/${templateId}/preview`, null, {
          params: {
            participant_id: participantId,
            case_id: caseId || selectedCaseId || undefined
          }
        });
        setSmsMessage(response.data.body);
        setSmsIsFreeForm(false);
      } catch (error) {
        toast.error('Failed to load template preview');
      } finally {
        setSmsPreviewLoading(false);
      }
    } else {
      setSmsMessage('');
      setSmsIsFreeForm(true);
    }
  };

  const handleEmailTemplateSelect = async (templateId) => {
    setEmailTemplateId(templateId);
    if (templateId && templateId !== 'free-form') {
      setEmailPreviewLoading(true);
      try {
        // Find the template in our loaded templates
        const template = emailTemplates.find(t => t.id === templateId);
        
        if (template?.source === 'admin_templates') {
          // For admin templates, we need to replace merge fields manually
          // or use a dedicated preview endpoint
          let subject = template.subject || '';
          let body = template.body || '';
          
          // Try to get preview with merge field replacement
          try {
            const previewResponse = await axios.post(`${API}/admin/templates/${templateId}/preview`, {
              participant_id: participantId,
              case_id: caseId || selectedCaseId || undefined
            });
            subject = previewResponse.data.subject || subject;
            body = previewResponse.data.body || body;
          } catch (previewError) {
            // If preview endpoint doesn't exist, use template as-is
            console.log('Using template without merge field replacement');
          }
          
          setEmailSubject(subject);
          setEmailBody(body);
          setEmailIsFreeForm(false);
        } else {
          // For message_templates, use the existing preview endpoint
          const response = await axios.post(`${API}/message-templates/${templateId}/preview`, null, {
            params: {
              participant_id: participantId,
              case_id: caseId || selectedCaseId || undefined
            }
          });
          setEmailSubject(response.data.subject || '');
          setEmailBody(response.data.body);
          setEmailIsFreeForm(false);
        }
      } catch (error) {
        toast.error('Failed to load template preview');
      } finally {
        setEmailPreviewLoading(false);
      }
    } else {
      setEmailSubject('');
      setEmailBody('');
      setEmailIsFreeForm(true);
    }
  };

  const handleSendSMS = async () => {
    if (!smsMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setSendingComm(true);
    try {
      await axios.post(`${API}/communications/sms/send`, {
        participant_id: participantId,
        case_id: caseId || selectedCaseId || null,
        phone_number: participantPhone,
        message: smsMessage,
        template_id: smsIsFreeForm ? null : smsTemplateId,
        is_free_form: smsIsFreeForm,
        from_number: selectedFromNumber || null
      });
      
      const compositionType = smsIsFreeForm ? 'free-form' : 'template-based';
      toast.success(`SMS sent successfully (${compositionType}). AI case note created automatically.`);
      setSmsMessage('');
      setSmsTemplateId('');
      setSmsIsFreeForm(true);
      setSmsDialogOpen(false);
      fetchCommunications();
      onCommunicationSent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setSendingComm(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) {
      toast.error('Please enter subject and body');
      return;
    }
    
    setSendingComm(true);
    try {
      await axios.post(`${API}/communications/email/send`, {
        participant_id: participantId,
        case_id: caseId || selectedCaseId || null,
        recipient_email: participantEmail,
        subject: emailSubject,
        body: emailBody,
        attachments: [],
        template_id: emailIsFreeForm ? null : emailTemplateId,
        is_free_form: emailIsFreeForm
      });
      
      const compositionType = emailIsFreeForm ? 'free-form' : 'template-based';
      toast.success(`Email sent successfully (${compositionType}). Case note created automatically.`);
      setEmailSubject('');
      setEmailBody('');
      setEmailTemplateId('');
      setEmailIsFreeForm(true);
      setEmailDialogOpen(false);
      fetchCommunications();
      onCommunicationSent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingComm(false);
    }
  };

  const handleInitiateCall = async () => {
    setSendingComm(true);
    try {
      const response = await axios.post(`${API}/communications/call/initiate`, {
        participant_id: participantId,
        case_id: caseId || selectedCaseId || null,
        phone_number: participantPhone,
        call_type: 'outbound',
        notes: callNotes,
        caller_id: selectedFromNumber || null  // Outbound number participant sees
      });
      
      setActiveCallId(response.data.call_id);
      setCallDialogOpen(false);
      setCompleteCallDialogOpen(true);
      toast.info('Call initiated. Complete the call details when finished.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate call');
    } finally {
      setSendingComm(false);
    }
  };

  const handleCompleteCall = async () => {
    if (!activeCallId) return;
    
    setSendingComm(true);
    try {
      await axios.post(`${API}/communications/call/${activeCallId}/complete`, null, {
        params: {
          duration: parseInt(callDuration) || 0,
          notes: callNotes
        }
      });
      
      toast.success('Call completed. AI case note created automatically.');
      setActiveCallId(null);
      setCallNotes('');
      setCallDuration('');
      setCompleteCallDialogOpen(false);
      fetchCommunications();
      onCommunicationSent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete call');
    } finally {
      setSendingComm(false);
    }
  };

  const getChannelIcon = (type) => {
    switch (type) {
      case 'sms': return <MessageSquare className="w-4 h-4" />;
      case 'email': return <Mail className="w-4 h-4" />;
      case 'phone': return <Phone className="w-4 h-4" />;
      default: return <MessageSquare className="w-4 h-4" />;
    }
  };

  const getDirectionIcon = (direction) => {
    return direction === 'outbound' || direction === 'sent' 
      ? <ArrowUpRight className="w-3 h-3" />
      : <ArrowDownLeft className="w-3 h-3" />;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'delivered':
      case 'sent':
      case 'completed':
        return <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400"><CheckCircle2 className="w-3 h-3 mr-1" />{status}</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"><AlertCircle className="w-3 h-3 mr-1" />{status}</Badge>;
      default:
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />{status}</Badge>;
    }
  };

  const getCompositionBadge = (compositionType) => {
    if (compositionType === 'template') {
      return <Badge variant="outline" className="text-xs"><FileText className="w-3 h-3 mr-1" />Template</Badge>;
    }
    return <Badge variant="outline" className="text-xs"><Edit3 className="w-3 h-3 mr-1" />Free-form</Badge>;
  };

  // Case selector for when initiated from participant profile
  const CaseSelector = () => {
    if (caseId) return null;
    
    return (
      <div className="space-y-2 mb-4">
        <Label>Attach to Case</Label>
        <Select value={selectedCaseId} onValueChange={setSelectedCaseId}>
          <SelectTrigger data-testid="case-selector">
            <SelectValue placeholder="Select case (or auto-create)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">Auto-select/create case</SelectItem>
            {openCases.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.id} - {c.service_type_name || c.case_type?.replace(/_/g, ' ') || 'Case'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  };

  return (
    <Card className="card-shadow" data-testid="communications-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Communications
            </CardTitle>
            <CardDescription>
              Send messages using templates or free-form composition
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => setSmsDialogOpen(true)}
              disabled={!participantPhone}
              data-testid="send-sms-btn"
            >
              <MessageSquare className="w-4 h-4 mr-1" />
              Text
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => setEmailDialogOpen(true)}
              disabled={!participantEmail}
              data-testid="send-email-btn"
            >
              <Mail className="w-4 h-4 mr-1" />
              Email
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => setCallDialogOpen(true)}
              disabled={!participantPhone}
              data-testid="call-btn"
            >
              <Phone className="w-4 h-4 mr-1" />
              Call
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Communication History */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="sms">SMS</TabsTrigger>
            <TabsTrigger value="email">Email</TabsTrigger>
            <TabsTrigger value="phone">Calls</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-0">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : communications.filter(c => activeTab === 'all' || c.type === activeTab).length > 0 ? (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {communications
                  .filter(c => activeTab === 'all' || c.type === activeTab)
                  .map((comm) => (
                    <div 
                      key={comm.id} 
                      className="p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                      data-testid={`comm-item-${comm.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <div className="p-2 rounded-full bg-primary/10">
                            {getChannelIcon(comm.type)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium capitalize">{comm.type}</span>
                              <span className="text-muted-foreground text-xs flex items-center gap-1">
                                {getDirectionIcon(comm.direction)}
                                {comm.direction}
                              </span>
                              {comm.composition_type && getCompositionBadge(comm.composition_type)}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {formatDateTime(comm.timestamp)}
                            </p>
                          </div>
                        </div>
                        {getStatusBadge(comm.delivery_status || comm.call_status)}
                      </div>
                      
                      {comm.template_name && (
                        <p className="mt-2 text-xs text-primary">
                          Template: {comm.template_name}
                        </p>
                      )}
                      
                      {comm.type === 'sms' && (
                        <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                          {comm.message}
                        </p>
                      )}
                      {comm.type === 'sms' && (comm.direction === 'outbound' || comm.direction === 'sent') && comm.from_number && (
                        <p className="mt-1 text-xs text-muted-foreground" data-testid={`comm-from-number-${comm.id}`}>
                          From: <span className="font-medium text-foreground">{comm.from_number}</span>
                        </p>
                      )}
                      {comm.type === 'email' && (
                        <p className="mt-2 text-sm text-muted-foreground">
                          <span className="font-medium">Subject:</span> {comm.subject}
                        </p>
                      )}
                      {comm.type === 'phone' && comm.duration && (
                        <p className="mt-2 text-sm text-muted-foreground">
                          Duration: {comm.duration} seconds
                        </p>
                      )}
                      
                      {/* Show error message for failed communications */}
                      {(comm.delivery_status === 'failed' || comm.call_status === 'failed') && comm.error_message && (
                        <div className="mt-2 p-2 bg-destructive/10 rounded border border-destructive/20">
                          <p className="text-xs text-destructive font-medium">Error:</p>
                          <p className="text-xs text-destructive/80">{comm.error_message}</p>
                        </div>
                      )}
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        {comm.sent_by_name || comm.initiated_by_name || 'System'}
                      </p>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No communications yet</p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* SMS Dialog */}
        <Dialog open={smsDialogOpen} onOpenChange={setSmsDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Send Text Message</DialogTitle>
              <DialogDescription>
                Send SMS to {participantName} at {participantPhone}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <CaseSelector />
              
              {/* Send From dropdown */}
              {outboundNumbers.length > 0 && (
                <div className="space-y-2">
                  <Label>Send From</Label>
                  <Select value={selectedFromNumber} onValueChange={setSelectedFromNumber}>
                    <SelectTrigger data-testid="sms-from-select">
                      <SelectValue placeholder="Select outbound number" />
                    </SelectTrigger>
                    <SelectContent>
                      {outboundNumbers.map((num) => (
                        <SelectItem key={num.id} value={num.phone_number}>
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4" />
                            {num.phone_number} {num.label && `(${num.label})`}
                            {num.is_default && <Badge variant="secondary" className="ml-1 text-xs">Default</Badge>}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Template or Free-form selection */}
              <div className="space-y-2">
                <Label>Composition Method</Label>
                <Select 
                  value={smsIsFreeForm ? 'free-form' : smsTemplateId} 
                  onValueChange={handleSmsTemplateSelect}
                >
                  <SelectTrigger data-testid="sms-template-select">
                    <SelectValue placeholder="Choose template or free-form" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free-form">
                      <div className="flex items-center gap-2">
                        <Edit3 className="w-4 h-4" />
                        Free-form Composition
                      </div>
                    </SelectItem>
                    {smsTemplates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          {template.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!smsIsFreeForm && (
                  <p className="text-xs text-muted-foreground">
                    Template selected. Merge fields have been auto-populated.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="sms-message">Message</Label>
                {smsPreviewLoading ? (
                  <div className="flex items-center justify-center h-24 border rounded-md">
                    <Loader2 className="w-5 h-5 animate-spin" />
                  </div>
                ) : (
                  <Textarea
                    id="sms-message"
                    value={smsMessage}
                    onChange={(e) => setSmsMessage(e.target.value)}
                    placeholder="Type your message..."
                    rows={4}
                    data-testid="sms-message-input"
                  />
                )}
                <p className="text-xs text-muted-foreground">
                  {smsMessage.length}/160 characters • {smsIsFreeForm ? 'Free-form' : 'Template-based'}
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSmsDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSendSMS} disabled={sendingComm} data-testid="send-sms-confirm">
                {sendingComm ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send SMS
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Email Dialog */}
        <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Send Email</DialogTitle>
              <DialogDescription>
                Send email to {participantName} at {participantEmail}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <CaseSelector />
              
              {/* Template or Free-form selection */}
              <div className="space-y-2">
                <Label>Composition Method</Label>
                <Select 
                  value={emailIsFreeForm ? 'free-form' : emailTemplateId} 
                  onValueChange={handleEmailTemplateSelect}
                >
                  <SelectTrigger data-testid="email-template-select">
                    <SelectValue placeholder="Choose template or free-form" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free-form">
                      <div className="flex items-center gap-2">
                        <Edit3 className="w-4 h-4" />
                        Free-form Composition
                      </div>
                    </SelectItem>
                    {emailTemplates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          {template.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!emailIsFreeForm && (
                  <p className="text-xs text-muted-foreground">
                    Template selected. Merge fields have been auto-populated.
                  </p>
                )}
              </div>

              {emailPreviewLoading ? (
                <div className="flex items-center justify-center h-48 border rounded-md">
                  <Loader2 className="w-5 h-5 animate-spin" />
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="email-subject">Subject</Label>
                    <Input
                      id="email-subject"
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      placeholder="Email subject..."
                      data-testid="email-subject-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email-body">Message</Label>
                    <Textarea
                      id="email-body"
                      value={emailBody}
                      onChange={(e) => setEmailBody(e.target.value)}
                      placeholder="Type your email..."
                      rows={8}
                      data-testid="email-body-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      {emailIsFreeForm ? 'Free-form composition' : 'Template-based composition'}
                    </p>
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSendEmail} disabled={sendingComm} data-testid="send-email-confirm">
                {sendingComm ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send Email
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Call Initiate Dialog */}
        <Dialog open={callDialogOpen} onOpenChange={setCallDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Initiate Phone Call</DialogTitle>
              <DialogDescription>
                Call {participantName} at {participantPhone}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <CaseSelector />
              
              {/* Outbound Caller ID dropdown - what participant sees */}
              {outboundNumbers.length > 0 && (
                <div className="space-y-2">
                  <Label>Outbound Caller ID (Participant Sees)</Label>
                  <Select value={selectedFromNumber} onValueChange={setSelectedFromNumber}>
                    <SelectTrigger data-testid="call-from-select">
                      <SelectValue placeholder="Select outbound number" />
                    </SelectTrigger>
                    <SelectContent>
                      {outboundNumbers.filter(num => num.is_call_capable !== false).map((num) => (
                        <SelectItem key={num.id} value={num.phone_number}>
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4" />
                            {num.phone_number} {num.label && `(${num.label})`}
                            {num.is_default && <Badge variant="secondary" className="ml-1 text-xs">Default</Badge>}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">This number will appear on the participant's caller ID</p>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="call-notes-pre">Pre-call Notes (optional)</Label>
                <Textarea
                  id="call-notes-pre"
                  value={callNotes}
                  onChange={(e) => setCallNotes(e.target.value)}
                  placeholder="Notes about the purpose of this call..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCallDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleInitiateCall} disabled={sendingComm} data-testid="initiate-call-confirm">
                {sendingComm ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Phone className="w-4 h-4 mr-2" />}
                Start Call
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Call Complete Dialog */}
        <Dialog open={completeCallDialogOpen} onOpenChange={setCompleteCallDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Complete Phone Call</DialogTitle>
              <DialogDescription>
                Enter details about the completed call
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="call-duration">Duration (seconds)</Label>
                <Input
                  id="call-duration"
                  type="number"
                  value={callDuration}
                  onChange={(e) => setCallDuration(e.target.value)}
                  placeholder="e.g., 300"
                  data-testid="call-duration-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="call-notes">Call Notes</Label>
                <Textarea
                  id="call-notes"
                  value={callNotes}
                  onChange={(e) => setCallNotes(e.target.value)}
                  placeholder="Summary of the call..."
                  rows={4}
                  data-testid="call-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCompleteCallDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCompleteCall} disabled={sendingComm} data-testid="complete-call-confirm">
                {sendingComm ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Complete Call
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default CommunicationsPanel;
